/**
 * SSE event handler functions for V3 state management.
 * Each handler takes event data and returns a state updater function
 * (or calls setState directly) to apply the event to React state.
 */

import {
    parseEventData,
    applyMapCellUpdates,
    mergeProfile,
    buildRegionCardUpdate,
    buildFullWorldState,
} from './v3StateHelpers';

// ─── v3Update event ─────────────────────────────────────────────────────────

/**
 * Handle a v3Update event. Supports "full" (complete replacement) and
 * "patch" (partial update) sub-types.
 *
 * @param {string|object} data - Raw event data
 * @param {Function} setState - React setState function
 */
export function handleV3Update(data, setState) {
    try {
        const update = parseEventData(data);
        console.log('[useV3State] Processing v3Update:', update);

        if (update.type === 'full' && update.data) {
            setState(prev => buildFullWorldState(update.data, prev));
        } else if (update.type === 'patch' && update.data) {
            const patch = update.data;
            setState(prev => {
                let newState = { ...prev };

                if (patch.profile) {
                    newState.profile = mergeProfile(prev.profile, patch.profile);
                }
                if (patch.position) {
                    newState.position = patch.position;
                }
                if (patch.regionCard) {
                    newState.regionCard = patch.regionCard;
                }
                if (patch.mapCells && Array.isArray(patch.mapCells)) {
                    newState.map = applyMapCellUpdates(prev.map, patch.mapCells);
                }

                return newState;
            });
        }
    } catch (e) {
        console.error('[useV3State] v3Update parse error:', e);
    }
}

// ─── v3Init event ───────────────────────────────────────────────────────────

/**
 * Handle a v3Init event. Sets the full world state.
 * Ignores the event if an MUI dialog is currently open (to avoid disrupting users).
 *
 * @param {string|object} data - Raw event data
 * @param {Function} setState - React setState function
 */
export function handleV3Init(data, setState) {
    // Check if any MUI dialog is open - if yes, ignore v3Init to not disrupt user
    // Note: V3OverlayView (settings, etc) uses custom overlay, not MuiDialog
    const dialogOpen = document.querySelector('.MuiDialog-root');
    if (dialogOpen) {
        console.log('[useV3State] Ignoring v3Init - MUI dialog is open');
        return;
    }

    try {
        const parsed = parseEventData(data);
        setState(prev => buildFullWorldState(parsed, prev));
    } catch (e) {
        console.error('[useV3State] v3Init parse error:', e);
    }
}

// ─── v3Patch event ──────────────────────────────────────────────────────────

/**
 * Handle a v3Patch event. Dispatches to sub-handlers based on patch.type.
 *
 * @param {string|object} data - Raw event data
 * @param {Function} setState - React setState function
 */
export function handleV3Patch(data, setState) {
    try {
        const patch = parseEventData(data);
        const timestamp = new Date().toISOString();
        console.log(`[useV3State] ${timestamp} Processing v3Patch:`, patch.type);

        setState(prev => applyV3Patch(prev, patch));
    } catch (e) {
        console.error('[useV3State] v3Patch parse error:', e);
    }
}

/**
 * Apply a v3Patch to the current state. Pure function: takes state and patch,
 * returns new state.
 *
 * @param {object} state - Current state
 * @param {object} patch - Patch object with type and data fields
 * @returns {object} Updated state
 */
export function applyV3Patch(state, patch) {
    switch (patch.type) {
        case 'profile':
            return {
                ...state,
                profile: mergeProfile(state.profile, patch.data),
            };

        case 'mapCell':
            if (!state.map) return state;
            return {
                ...state,
                map: state.map.map(cell =>
                    (cell.x === patch.data.x && cell.y === patch.data.y)
                        ? patch.data
                        : cell
                ),
            };

        case 'regionCard': {
            const updates = patch.data;

            console.log('[useV3State] regionCard patch received:', {
                hasMovementProgress: 'movementProgress' in updates,
                movementProgressValue: updates.movementProgress,
                allUpdates: updates,
            });

            const newRegionCard = buildRegionCardUpdate(state.regionCard, updates);

            console.log('[useV3State] New regionCard state:', {
                hasMovementProgress: !!newRegionCard.movementProgress,
                movementProgress: newRegionCard.movementProgress,
            });

            return {
                ...state,
                regionCard: newRegionCard,
                position: patch.position || state.position,
            };
        }

        case 'position':
        case 'playerPosition':
            console.log('[useV3State] Position update received:', patch.data);
            return {
                ...state,
                position: patch.data,
            };

        case 'mapCells':
            if (!state.map || !Array.isArray(patch.data)) return state;
            return {
                ...state,
                map: applyMapCellUpdates(state.map, patch.data),
            };

        case 'worldUpdate': {
            console.log('[useV3State] Processing unified worldUpdate');
            const worldData = patch.data;
            let newState = { ...state };

            if (worldData.profile) {
                console.log('[useV3State] World update includes profile changes');
                newState.profile = mergeProfile(state.profile, worldData.profile);
            }
            if (worldData.region) {
                console.log('[useV3State] World update includes region changes');
                newState.regionCard = worldData.region;
            }
            if (worldData.position) {
                console.log('[useV3State] World update includes position changes');
                newState.position = worldData.position;
            }
            if (worldData.mapCells && Array.isArray(worldData.mapCells)) {
                console.log('[useV3State] World update includes', worldData.mapCells.length, 'map cell changes');
                newState.map = applyMapCellUpdates(state.map, worldData.mapCells);
            }

            return newState;
        }

        case 'movementUpdate': {
            console.log('[useV3State] Processing unified movementUpdate');
            const movementData = patch.data;

            if (movementData.profile) {
                console.log('[useV3State] Movement update includes profile:', {
                    steps: movementData.profile.steps,
                    maxSteps: movementData.profile.maxSteps,
                    mana: movementData.profile.mana,
                    maxMana: movementData.profile.maxMana,
                });
            }

            return {
                ...state,
                regionCard: movementData.regionCard,
                position: movementData.position,
                map: applyMapCellUpdates(state.map, movementData.mapCells),
                profile: mergeProfile(state.profile, movementData.profile),
            };
        }

        default:
            console.warn('[useV3State] Unknown patch type:', patch.type);
            return state;
    }
}
